import React, { useState } from 'react';
import {
 Bell,
 CheckCircle,
 XCircle,
 Clock,
 Calendar,
 Plus,
 Trash2,
 Eye,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';
import { formatDate, formatTime } from '../../utils/dateUtils';
import AdminBroadcastModal from '../../components/admin/AdminBroadcastModal';
import AdminNotificationDetailModal from '../../components/admin/AdminNotificationDetailModal';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminNotifications = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [detailNotification, setDetailNotification] = useState(null);
 const [selectedNotifications, setSelectedNotifications] = useState([]);
 const {
 notifications,
 isLoading,
 error,
 filters,
 readStatusOptions,
 sentStatusOptions,
 handleFilterChange,
 handleSearch,
 statistics,
 deleteNotification,
 isDeleting,
 refetch
 } = useAdminNotifications();

 const handleRowSelect = (selectedRows) => {
 setSelectedNotifications(selectedRows);
 };

 const handleBulkDelete = async () => {
 if (selectedNotifications.length === 0) return;

 if (window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notification(s)?`)) {
 for (const notification of selectedNotifications) {
 await deleteNotification(notification.id);
 }
 setSelectedNotifications([]);
 }
 };

 const columns = [
 {
 key: 'user',
 label: 'User',
 sortable: true,
 width: 'w-48',
 render: (notification) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {notification.user?.name || 'All Users'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {notification.user?.email || ''}
 </p>
 </div>
 )
 },
 {
 key: 'type',
 label: 'Type',
 sortable: true,
 width: 'w-32',
 render: (notification) => (
 <span className="text-sm font-medium text-ink capitalize">
 {notification.type || 'N/A'}
 </span>
 )
 },
 {
 key: 'title',
 label: 'Title',
 sortable: true,
 width: 'w-48',
 render: (notification) => (
 <p className="text-sm font-medium text-ink truncate max-w-[120px]" title={notification.title}>
 {notification.title?.length > 20 ? notification.title.substring(0, 20) + '...' : notification.title || 'N/A'}
 </p>
 )
 },
 {
 key: 'message',
 label: 'Message',
 sortable: false,
 width: 'w-64',
 render: (notification) => (
 <p className="text-sm text-ink truncate max-w-[150px]" title={notification.message}>
 {notification.message?.length > 25 ? notification.message.substring(0, 25) + '...' : notification.message || 'N/A'}
 </p>
 )
 },
 {
 key: 'created_at',
 label: 'Date',
 sortable: true,
 width: 'w-40',
 render: (notification) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(notification.created_at, 'medium')}
 </div>
 <div className="text-xs text-ink-tertiary">
 {formatTime(notification.created_at)}
 </div>
 </div>
 </div>
 )
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (notification) => (
 <div className="flex flex-col gap-1">
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ notification.is_read ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800' }`}>
 {notification.is_read ? 'Read' : 'Unread'}
 </span>
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ notification.is_sent ? 'bg-surface-muted text-ink' : 'bg-gray-100 text-gray-800' }`}>
 {notification.is_sent ? 'Sent' : 'Pending'}
 </span>
 </div>
 )
 }
 ];

 const handleViewNotification = (notification) => {
 setDetailNotification(notification);
 };

 const handleDeleteFromModal = (id) => {
 deleteNotification(id, {
 onSuccess: () => {
 setDetailNotification(null);
 setSelectedNotifications((prev) => prev.filter((n) => n.id !== id));
 },
 });
 };

 const actions = [
 {
 label: 'View',
 icon: Eye,
 variant: 'default',
 onClick: handleViewNotification,
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (notification) => {
 if (window.confirm('Are you sure you want to delete this notification?')) {
 deleteNotification(notification.id);
 }
 }
 }
 ];

 const { totalNotifications, unreadNotifications, sentNotifications } = statistics;

 if (error) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Notifications</h3>
 <p className="text-red-600 text-sm">
 {error?.response?.data?.message || 'Failed to load notifications. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Notifications"
 subtitle="Manage and send platform notifications."
 actions={
 <div className="flex items-center gap-2">
 {selectedNotifications.length > 0 && (
 <Button
 onClick={handleBulkDelete}
 variant="danger"
 className="rounded-md flex items-center gap-2"
 disabled={isDeleting}
 >
 <Trash2 className="w-4 h-4" />
 Delete ({selectedNotifications.length})
 </Button>
 )}
 <Button
 onClick={() => setShowCreateModal(true)}
 className="rounded-md flex items-center gap-2"
 >
 <Plus className="w-4 h-4" />
 Create Notification
 </Button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.notifications.title}
 steps={ADMIN_PAGE_GUIDES.notifications.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Notifications</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{totalNotifications}</p>
 </div>
 <Bell className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Unread</p>
 <p className="text-2xl font-bold text-amber-600 mt-1 tabular-nums">{unreadNotifications}</p>
 </div>
 <Clock className="w-8 h-8 text-amber-500" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Sent</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">{sentNotifications}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 </div>

 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
 <SearchSelect
 label="Read Status"
 value={filters.is_read}
 onChange={(value) => handleFilterChange('is_read', value)}
 options={readStatusOptions}
 />
 <SearchSelect
 label="Sent Status"
 value={filters.is_sent}
 onChange={(value) => handleFilterChange('is_sent', value)}
 options={sentStatusOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading notifications…</p>
 </div>
 ) : (
 <DataTable
 data={notifications}
 columns={columns}
 actions={actions}
 isLoading={false}
 selectable={true}
 onRowSelect={handleRowSelect}
 onRowClick={handleViewNotification}
 searchPlaceholder="Search notifications by title, message, type, or user..."
 onSearch={handleSearch}
 emptyMessage="No notifications found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 <AdminBroadcastModal
 isOpen={showCreateModal}
 onClose={() => setShowCreateModal(false)}
 onSuccess={() => {
 refetch();
 }}
 />

 <AdminNotificationDetailModal
 notification={detailNotification}
 onClose={() => setDetailNotification(null)}
 onDelete={handleDeleteFromModal}
 isDeleting={isDeleting}
 />
 </div>
 );
};

export default AdminNotifications;
